/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import Cookies from 'js-cookie'
import { fromEvent, Subject, shareReplay, distinctUntilChanged, startWith } from 'rxjs'
import { v4 } from 'uuid'
import { useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'

import type { AnalyticsClient } from '@elastic/ebt'

const HTML_ATTRIBUTES_TO_REMOVE = [
  'data-href',
  'data-ech-series-name',
  'data-provider-id',
  'data-rfd-drag-handle-draggable-id',
  'data-rfd-droppable-id',
  'data-rfd-draggable-id',
  'href',
  'value',
  'title',
]

export function trackClicks(analytics: AnalyticsClient, isDevMode: boolean) {
  analytics.registerEventType<{ target: string[] }>({
    eventType: 'click',
    schema: {
      target: {
        type: 'array',
        items: {
          type: 'keyword',
          _meta: {
            description:
              'The attributes of the clicked element and all its parents in the form `{attr.name}={attr.value}`. It allows finding the clicked elements by looking up its attributes like "data-test-subj=my-button".',
          },
        },
      },
    },
  })

  return fromEvent(window, 'click').subscribe((event) => {
    try {
      const target = event.target as HTMLElement
      analytics.reportEvent('click', { target: getTargetDefinition(target) })
    } catch (error) {
      if (isDevMode) {
        // Defensively log the error in dev mode to catch any potential bugs.

        console.error(`Failed to report the click event`, { event, error })
      }
    }
  })
}

function getTargetDefinition(target: HTMLElement): string[] {
  const definitions: string[] = []
  let currentElement: HTMLElement | null = target

  while (currentElement) {
    // Add filtered attributes first to maintain order
    for (const attr of Array.from(currentElement.attributes)) {
      if (!HTML_ATTRIBUTES_TO_REMOVE.includes(attr.name)) {
        definitions.unshift(`${attr.name}=${attr.value}`.slice(0, 256))
      }
    }

    // Then add the tag name
    definitions.unshift(currentElement.tagName)

    // Move to the parent element
    currentElement = currentElement.parentElement
  }

  return definitions
}

const PRIMARY_ENV_LABELS = ['qa', 'staging', 'cloud']
const SECONDARY_ENV_LABELS = ['admin', 'console']

export function getCookieDomain(hostname) {
  if (
    hostname === 'localhost' ||
    /^[\d.]+$/.test(hostname) || // IPv4
    /^\[.*\]$/.test(hostname) // IPv6
  ) {
    return undefined
  }

  const parts = hostname.split('.')

  // Find the first env label in the hostname
  const primaryIdx = parts.findIndex((part) => PRIMARY_ENV_LABELS.includes(part))
  const secondaryIdx = parts.findIndex((part) => SECONDARY_ENV_LABELS.includes(part))

  if (primaryIdx === -1 && secondaryIdx === -1) {
    // fallback: use last 3 parts
    if (parts.length < 3) {
      return undefined
    }

    return '.' + parts.slice(-3).join('.')
  }

  // Return everything from the env label to the end
  return '.' + parts.slice(primaryIdx === -1 ? secondaryIdx : primaryIdx).join('.')
}

export function getSessionId(): string {
  const sessionId = Cookies.get('ebtSessionId') ?? v4()
  Cookies.set('ebtSessionId', sessionId, {
    path: '/',
    domain: getCookieDomain(window.location.hostname),
    secure: true,
  })
  return sessionId
}

export interface Location {
  pathname: string
  hash: string
}

// Convert location to URL
const locationToUrl = (location) => location.pathname + location.search

// Custom hook that returns an observable for location changes
export const useLocationObservable = () => {
  const location = useLocation()

  const [subject] = useState(new Subject<string>())
  const [observable] = useState(
    subject.pipe(
      startWith(locationToUrl(location)), // Emit initial location value
      distinctUntilChanged(), // Ensure only distinct locations
      shareReplay(1), // Share the latest value with new subscribers
    ),
  )

  useEffect(() => {
    // Emit new location value when location changes
    subject.next(locationToUrl(location))
  }, [location, subject])

  // Return the observable to be used by subscribers
  return observable
}
